import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function Grid_Container() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🟦 CSS Grid Container: Complete Guide</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        A CSS Grid container defines the context for all its grid items. By
        configuring the container properties, you control the layout, alignment,
        spacing, and flow of the items.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#1e3a8a" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code>display: grid</code> or <code>inline-grid</code> defines a
            grid container.
          </li>
          <li>
            <code>grid-template-columns</code> / <code>grid-template-rows</code>{" "}
            sets explicit sizes.
          </li>
          <li>
            <code>grid-auto-rows</code> / <code>grid-auto-columns</code> sets
            implicit sizes for additional items.
          </li>
          <li>
            <code>gap</code> sets spacing between rows and columns.
          </li>
          <li>
            Alignment properties: <code>justify-items</code>,{" "}
            <code>align-items</code>, <code>justify-content</code>,{" "}
            <code>align-content</code>, <code>place-items</code>,{" "}
            <code>place-content</code>.
          </li>
          <li>
            <code>grid-auto-flow</code> controls the placement direction: row,
            column, dense.
          </li>
        </ul>
      </div>

      {/* Example 1: Basic Grid Container */}
      <h2 className="section-title">1️⃣ Basic Grid Container</h2>
      <CodeCopyBlock
        code={`.grid-basic-container {
  display: grid;
  grid-template-columns: 100px 100px 100px;
  grid-template-rows: 100px 100px;
  gap: 10px;
}`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "100px 100px 100px",
          gridTemplateRows: "100px 100px",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            style={{
              background: "#3b82f6",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
            }}
          >
            {i + 1}
          </div>
        ))}
      </div>

      {/* Example 2: Alignment Properties */}
      <h2 className="section-title">2️⃣ Alignment Properties</h2>
      <CodeCopyBlock
        code={`.grid-align {
  display: grid;
  grid-template-columns: repeat(3, 100px);
  grid-template-rows: repeat(2, 100px);
  gap: 10px;
  justify-items: center; /* horizontal alignment of items */
  align-items: center;   /* vertical alignment of items */
  justify-content: center; /* container horizontal alignment */
  align-content: center;   /* container vertical alignment */
}`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3,100px)",
          gridTemplateRows: "repeat(2,100px)",
          gap: "10px",
          justifyItems: "center",
          alignItems: "center",
          justifyContent: "center",
          alignContent: "center",
          height: "250px",
          marginBottom: "2rem",
          background: "#e0f2fe",
        }}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            style={{
              background: "#10b981",
              color: "white",
              padding: "20px",
              borderRadius: "8px",
            }}
          >
            {i + 1}
          </div>
        ))}
      </div>

      {/* Example 3: Implicit Grid with grid-auto-rows / columns */}
      <h2 className="section-title">3️⃣ Implicit Grid</h2>
      <CodeCopyBlock
        code={`.grid-auto {
  display: grid;
  grid-template-columns: 100px 100px;
  grid-auto-rows: 80px;
  gap: 10px;
}`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "100px 100px",
          gridAutoRows: "80px",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            style={{
              background: "#f97316",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
            }}
          >
            {i + 1}
          </div>
        ))}
      </div>

      {/* Example 4: grid-auto-flow */}
      <h2 className="section-title">4️⃣ Grid Auto Flow</h2>
      <CodeCopyBlock
        code={`.grid-flow {
  display: grid;
  grid-template-columns: 100px 100px;
  grid-auto-rows: 100px;
  gap: 10px;
  grid-auto-flow: row dense;
}`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "100px 100px",
          gridAutoRows: "100px",
          gap: "10px",
          gridAutoFlow: "row dense",
          marginBottom: "2rem",
        }}
      >
        {Array.from({ length: 7 }).map((_, i) => (
          <div
            key={i}
            style={{
              background: "#3b82f6",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
            }}
          >
            {i + 1}
          </div>
        ))}
      </div>

      {/* Example 5: Inline Grid */}
      <h2 className="section-title">5️⃣ Inline Grid</h2>
      <CodeCopyBlock
        code={`.inline-grid {
  display: inline-grid;
  grid-template-columns: repeat(3, 80px);
  grid-template-rows: 80px;
  gap: 10px;
}`}
      />
      <div
        style={{
          display: "inline-grid",
          gridTemplateColumns: "repeat(3, 80px)",
          gridTemplateRows: "80px",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            style={{
              background: "#10b981",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
            }}
          >
            {i + 1}
          </div>
        ))}
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fcd34d)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Combine container properties with grid lines for precise placement.
          </li>
          <li>
            Use responsive units like <code>fr</code> and <code>minmax()</code>{" "}
            for flexible layouts.
          </li>
          <li>
            Experiment with different <code>justify-items</code> and{" "}
            <code>align-items</code> values.
          </li>
          <li>
            Create complex dashboards, galleries, and forms using advanced
            container setups.
          </li>
        </ul>
      </div>
    </div>
  );
}
